/**
 * A galeria de um grupo de fotos do signatário (E80.F1.T06 — ADR-0079 §2.1).
 *
 * Componente próprio, e não um bloco dentro da seção: são dois grupos com o
 * mesmo comportamento, e a seção que os monta ficaria acima do limite de 200
 * linhas do handbook §8 só por repetir a galeria duas vezes.
 *
 * O que a galeria promete: **a escolhida é visível sem precisar clicar**, e
 * trocá-la é um clique que não apaga nada — a preterida continua na lista, com
 * o código de conferência dela à vista (§2.1).
 */
import { Crop, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { FotoDoSignatarioData, GrupoDeFotosData } from '@/lib/fotos_do_signatario';

import { FotoDoSignatarioThumb } from './FotoDoSignatarioThumb';

const t = ptBR.exameEletronico.fotosDoSignatario;

export type AcaoNaFoto = 'escolhendo' | 'removendo';

interface FotoCardProps {
  caseId: string;
  foto: FotoDoSignatarioData;
  acao: AcaoNaFoto | null;
  bloqueado: boolean;
  onEscolher: (foto: FotoDoSignatarioData) => void;
  onRecortar: (foto: FotoDoSignatarioData) => void;
  onRemover: (foto: FotoDoSignatarioData) => void;
}

function FotoCard({
  caseId,
  foto,
  acao,
  bloqueado,
  onEscolher,
  onRecortar,
  onRemover,
}: FotoCardProps): JSX.Element {
  return (
    <li
      className={`flex gap-sc-3 border p-sc-3 ${
        foto.eleita ? 'border-accent bg-accent/[0.06]' : 'border-border'
      }`}
      data-testid={`foto-${foto.id}`}
      data-eleita={foto.eleita}
    >
      <FotoDoSignatarioThumb caseId={caseId} foto={foto} />

      <div className="min-w-0 flex-1 space-y-sc-1">
        <p className="truncate text-body-sm text-fg" title={foto.nome_arquivo}>
          {foto.nome_arquivo}
        </p>
        {foto.eleita ? (
          <p className="text-caption text-accent-700" data-testid={`foto-${foto.id}-escolhida`}>
            {t.escolhida}
          </p>
        ) : null}
        {foto.bounding_box !== null ? (
          <p className="text-caption text-fg-dim">{t.recortada}</p>
        ) : null}
        <p className="break-all text-caption text-fg-dim">
          {t.codigoDeConferencia.replace('{valor}', foto.content_sha256)}
        </p>

        <div className="flex flex-wrap items-center gap-sc-1 pt-sc-1">
          {foto.eleita ? null : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={bloqueado}
              onClick={() => onEscolher(foto)}
              data-testid={`foto-${foto.id}-escolher`}
            >
              {acao === 'escolhendo' ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  {t.escolhendo}
                </>
              ) : (
                t.escolher
              )}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={bloqueado}
            onClick={() => onRecortar(foto)}
            data-testid={`foto-${foto.id}-recortar`}
          >
            <Crop className="h-3.5 w-3.5" aria-hidden />
            {t.recortar}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={bloqueado}
            onClick={() => onRemover(foto)}
            data-testid={`foto-${foto.id}-remover`}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            {acao === 'removendo' ? t.removendo : t.remover}
          </Button>
        </div>
      </div>
    </li>
  );
}

export interface GrupoDeFotosProps {
  caseId: string;
  grupo: GrupoDeFotosData;
  enviando: boolean;
  bloqueado: boolean;
  acaoPorFoto: Readonly<Record<string, AcaoNaFoto>>;
  onAnexar: (arquivo: File) => void;
  onEscolher: (foto: FotoDoSignatarioData) => void;
  onRecortar: (foto: FotoDoSignatarioData) => void;
  onRemover: (foto: FotoDoSignatarioData) => void;
}

export function GrupoDeFotos({
  caseId,
  grupo,
  enviando,
  bloqueado,
  acaoPorFoto,
  onAnexar,
  onEscolher,
  onRecortar,
  onRemover,
}: GrupoDeFotosProps): JSX.Element {
  const rotulos = t.grupos[grupo.grupo];
  const inputId = `foto-input-${grupo.grupo}`;

  return (
    <section
      className="space-y-sc-2"
      data-testid={`grupo-de-fotos-${grupo.grupo}`}
      data-eleita={grupo.eleita_id ?? ''}
      aria-labelledby={`grupo-titulo-${grupo.grupo}`}
    >
      <div>
        <h4 id={`grupo-titulo-${grupo.grupo}`} className="text-body-md text-fg">
          {rotulos.titulo}
        </h4>
        <p className="max-w-prose text-body-sm text-fg-muted">{rotulos.descricao}</p>
      </div>

      {grupo.fotos.length === 0 ? (
        <p className="text-body-sm text-fg-dim" data-testid={`grupo-vazio-${grupo.grupo}`}>
          {rotulos.vazio}
        </p>
      ) : (
        <ul className="space-y-sc-2">
          {grupo.fotos.map((foto) => (
            <FotoCard
              key={foto.id}
              caseId={caseId}
              foto={foto}
              acao={acaoPorFoto[foto.id] ?? null}
              bloqueado={bloqueado}
              onEscolher={onEscolher}
              onRecortar={onRecortar}
              onRemover={onRemover}
            />
          ))}
        </ul>
      )}

      {grupo.fotos.length > 0 && grupo.eleita_id === null ? (
        <p
          role="status"
          className="text-body-sm text-fg-muted"
          data-testid={`grupo-sem-escolhida-${grupo.grupo}`}
        >
          {t.semEscolhida}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-sc-2">
        <input
          id={inputId}
          type="file"
          accept="image/png,image/jpeg"
          className="sr-only"
          data-testid={`anexar-foto-input-${grupo.grupo}`}
          disabled={bloqueado}
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            if (arquivo !== undefined) onAnexar(arquivo);
            // Permite reenviar o MESMO arquivo depois de corrigi-lo fora daqui.
            evento.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={bloqueado}
          onClick={() => document.getElementById(inputId)?.click()}
          data-testid={`anexar-foto-${grupo.grupo}`}
        >
          {enviando ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {t.anexando}
            </>
          ) : (
            t.anexar
          )}
        </Button>
        <span className="text-caption text-fg-dim">{t.formatosAceitos}</span>
      </div>
    </section>
  );
}
